import React, { useEffect, useRef, useState } from 'react';

import Toggle from '../../DesignSystem/Toggle/Toggle';
import Radio from '../../DesignSystem/Radio/Radio';
import Dropdown from '../../DesignSystem/Dropdown/Dropdown';
import {
    fetchParentChannels,
    fetchEnvironments,
    ParentChannelOption,
    EnvironmentOption,
} from './Services/syncEnvironmentsService';
import { SkuSyncEnvironment } from './types';

const ALL_VALUE = 'ALL';

type SyncMode = 'all' | 'specific';

type Row = {
    id: number;
    channel: string; // parent channel id
    env: string; // child channel key, or ALL_VALUE
};

interface SyncToEnvironmentsProps {
    partyId?: string;
    domainId?: string;
    /** Saved value from the connector config; undefined when never configured. */
    initialValue?: SkuSyncEnvironment[];
    /** Emits the payload shape persisted as `sku_sync_environments`. */
    /** `pending` is true while Choose Specific has nothing picked yet — not a selection to persist. */
    /** `edits` counts the user's own changes (0 until they touch the section) — the signal to save. */
    onChange: (value: SkuSyncEnvironment[], meta?: { pending: boolean; edits: number }) => void;
    /** The environment being imported into. Its products are already SKU-mapped there, so it is never a sync target. */
    currentChannelKey?: string;
    disabled?: boolean;
}

let nextRowId = 0;
const emptyRow = (): Row => ({ id: ++nextRowId, channel: '', env: '' });

type Taken = { fullChannels: Set<string>; envs: Record<string, Set<string>> };

const SyncToEnvironments: React.FC<SyncToEnvironmentsProps> = ({
    partyId,
    domainId,
    initialValue,
    onChange,
    currentChannelKey,
    disabled = false,
}) => {
    const [enabled, setEnabled] = useState(false);
    const [edits, setEdits] = useState(0);
    const markEdited = () => setEdits((count) => count + 1);
    const [mode, setMode] = useState<SyncMode>('all');
    const [rows, setRows] = useState<Row[]>([emptyRow()]);
    const [parentChannels, setParentChannels] = useState<ParentChannelOption[]>([]);
    const [envByChannel, setEnvByChannel] = useState<Record<string, EnvironmentOption[]>>({});

    // Keep the latest onChange without retriggering the emit effect.
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const targetEnvs = (channelId: string): EnvironmentOption[] =>
        (envByChannel[channelId] || []).filter((env) => env.key !== currentChannelKey);

    // Hydrate internal state from the saved config exactly once.
    const didHydrate = useRef(false);
    useEffect(() => {
        if (didHydrate.current || initialValue == null) return;
        didHydrate.current = true;

        // `sku_sync_environments` stores only concrete channel/env pairs, so a saved
        // config rehydrates as explicit "Choose Specific" rows. If those pairs turn
        // out to cover every combination, the effect below upgrades the view to
        // "Sync All" once the full universe of environments is known.
        const saved = initialValue.filter((v) => v.env !== currentChannelKey);
        if (saved.length === 0) {
            setEnabled(false);
            setMode('all');
            setRows([emptyRow()]);
        } else {
            setEnabled(true);
            setMode('specific');
            setRows(saved.map((v) => ({ id: ++nextRowId, channel: v.channel, env: v.env })));
        }
    }, [initialValue]);

    // A "Sync All" selection is persisted as the fully-expanded set of pairs, which
    // would otherwise rehydrate as "Choose Specific". Once every channel's
    // environments are loaded, compare the saved pairs against the full universe and
    // switch back to "Sync All" when they cover it exactly. Runs at most once.
    const didDetectAll = useRef(false);
    useEffect(() => {
        if (didDetectAll.current || !didHydrate.current) return;
        if (mode !== 'specific' || initialValue == null || initialValue.length === 0) return;
        if (parentChannels.length === 0) return;
        // Wait until every channel's environments have been fetched.
        if (!parentChannels.every((c) => envByChannel[c.id] !== undefined)) return;

        const universe = new Set<string>();
        parentChannels.forEach((c) => {
            targetEnvs(c.id).forEach((env) => universe.add(`${c.id}|${env.key}`));
        });
        if (universe.size === 0) return;

        didDetectAll.current = true;
        const saved = new Set(
            initialValue.filter((v) => v.env !== currentChannelKey).map((v) => `${v.channel}|${v.env}`)
        );
        const coversAll = saved.size === universe.size && Array.from(universe).every((k) => saved.has(k));
        if (coversAll) setMode('all');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialValue, parentChannels, envByChannel, mode, currentChannelKey]);

    // Load parent channels for the current party + domain.
    useEffect(() => {
        if (!partyId || !domainId) return;
        fetchParentChannels(partyId, domainId).then((res) => {
            if (res.success && res.data) setParentChannels(res.data);
        });
    }, [partyId, domainId]);

    // Preload every channel's environments once, as soon as the channel list is
    // known — never on selection. Choosing channel/environment pairs must stay
    // purely local and fire no API calls, so all environment data is fetched here
    // and cached in `envByChannel` for the dropdowns and pair expansion.
    useEffect(() => {
        if (!partyId || parentChannels.length === 0) return;
        parentChannels.forEach((channel) => {
            if (envByChannel[channel.id]) return;
            fetchEnvironments(partyId, channel.id).then((res) => {
                if (res.success && res.data) {
                    setEnvByChannel((prev) => ({ ...prev, [channel.id]: res.data! }));
                }
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [partyId, parentChannels]);

    // Emit the persisted payload whenever the selection changes. "Sync All" and a
    // per-channel "All" environment are expanded into concrete { channel, env } pairs.
    useEffect(() => {
        const pairs: SkuSyncEnvironment[] = [];

        const addChannelAllEnvs = (channelId: string) => {
            targetEnvs(channelId).forEach((env) => {
                pairs.push({ channel: channelId, env: env.key });
            });
        };

        if (enabled) {
            if (mode === 'all') {
                parentChannels.forEach((channel) => addChannelAllEnvs(channel.id));
            } else {
                rows.forEach((row) => {
                    if (!row.channel) return;
                    if (row.env === ALL_VALUE) {
                        addChannelAllEnvs(row.channel);
                    } else if (row.env && row.env !== currentChannelKey) {
                        pairs.push({ channel: row.channel, env: row.env });
                    }
                });
            }
        }

        // Dedupe combinations that could repeat across rows.
        const seen = new Set<string>();
        const value = pairs.filter((pair) => {
            const key = `${pair.channel}|${pair.env}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });

        onChangeRef.current(value, { pending: enabled && mode === 'specific' && value.length === 0, edits });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, mode, rows, parentChannels, envByChannel, currentChannelKey, edits]);

    const handleChannelChange = (rowId: number, value: string) => {
        markEdited();
        setRows((prev) => prev.map((row) => (row.id === rowId ? { ...row, channel: value, env: '' } : row)));
    };

    const handleEnvChange = (rowId: number, value: string) => {
        markEdited();
        setRows((prev) => prev.map((row) => (row.id === rowId ? { ...row, env: value } : row)));
    };

    const selectMode = (next: SyncMode) => {
        if (next === mode) return;
        markEdited();
        if (next === 'specific') setRows([emptyRow()]);
        setMode(next);
    };

    const removeRow = (rowId: number) => {
        markEdited();
        setRows((prev) => (prev.length > 1 ? prev.filter((row) => row.id !== rowId) : prev));
    };

    const takenByOtherRows = (rowId?: number): Taken => {
        const taken: Taken = { fullChannels: new Set(), envs: {} };
        rows.forEach((row) => {
            if (row.id === rowId || !row.channel) return;
            if (row.env === ALL_VALUE) {
                taken.fullChannels.add(row.channel);
            } else if (row.env) {
                (taken.envs[row.channel] = taken.envs[row.channel] || new Set()).add(row.env);
            }
        });
        return taken;
    };

    const channelHasRoom = (channelId: string, taken: Taken) => {
        if (taken.fullChannels.has(channelId)) return false;
        if (envByChannel[channelId] === undefined) return true;
        return targetEnvs(channelId).some((env) => !taken.envs[channelId]?.has(env.key));
    };

    const getChannelOptions = (row: Row) => {
        const taken = takenByOtherRows(row.id);
        return parentChannels
            .filter((c) => c.id === row.channel || channelHasRoom(c.id, taken))
            .map((c) => ({ label: c.name, value: c.id }));
    };

    const getEnvOptions = (row: Row) => {
        if (!row.channel) return [];
        const pickedElsewhere = takenByOtherRows(row.id).envs[row.channel];
        const envs = targetEnvs(row.channel).filter((env) => env.key === row.env || !pickedElsewhere?.has(env.key));
        const allOption = pickedElsewhere?.size ? [] : [{ label: 'All', value: ALL_VALUE }];
        return [...allOption, ...envs.map((e) => ({ label: e.name, value: e.key }))];
    };

    const canAddRow = (() => {
        const taken = takenByOtherRows();
        return parentChannels.some((c) => channelHasRoom(c.id, taken));
    })();

    const addRow = () => {
        if (canAddRow) setRows((prev) => [...prev, emptyRow()]);
    };

    return (
        <div className="tw-rounded-lg tw-border tw-border-edge tw-p-4" data-testid="connectors-sync-to-environments">
            <div className="tw-flex tw-items-center tw-justify-between">
                <span className="tw-text-base tw-font-semibold tw-text-ink">Sync to Environments</span>
                <Toggle
                    label=""
                    checked={enabled}
                    onChange={() => {}}
                    disabled
                    data-testid="connectors-sync-toggle"
                />
            </div>

            <p className="tw-mt-1 tw-mb-0 tw-text-sm tw-text-ink-secondary">
                Automatically map every product imported here to the selected channel-environment
                combinations — no manual sync needed later.
            </p>

            {enabled && (
                <div className="tw-mt-3">
                    <div
                        className={`tw-flex tw-items-center tw-gap-3 ${disabled ? '' : 'tw-cursor-pointer'}`}
                        onClick={() => !disabled && selectMode('all')}
                    >
                        <Radio
                            label=""
                            name="sync-to-environments-mode"
                            value="all"
                            checked={mode === 'all'}
                            onChange={() => selectMode('all')}
                            disabled={disabled}
                        />
                        <span className="tw-text-ink">
                            <strong>Sync All</strong> - Every channel, every environment
                        </span>
                    </div>

                    <div
                        className={`tw-flex tw-items-center tw-gap-3 tw-mt-2 ${disabled ? '' : 'tw-cursor-pointer'}`}
                        onClick={() => !disabled && selectMode('specific')}
                    >
                        <Radio
                            label=""
                            name="sync-to-environments-mode"
                            value="specific"
                            checked={mode === 'specific'}
                            onChange={() => selectMode('specific')}
                            disabled={disabled}
                        />
                        <span className="tw-text-ink">
                            <strong>Choose Specific</strong> - Pick channel + environment pairs
                        </span>
                    </div>

                    {mode === 'specific' && (
                        <div className="tw-mt-3">
                            {rows.map((row, index) => (
                                <div key={row.id} className="tw-flex tw-items-end tw-gap-3 tw-mb-3" data-testid={`connectors-sync-row-${row.id}`}>
                                    <Dropdown
                                        label={index === 0 ? 'Channel' : ''}
                                        className="tw-flex-1"
                                        placeholder="Select"
                                        usePortal
                                        disabled={disabled}
                                        options={getChannelOptions(row)}
                                        value={row.channel}
                                        onChange={(value) => handleChannelChange(row.id, String(value))}
                                        data-testid={`connectors-sync-row-${row.id}-channel`}
                                    />
                                    <Dropdown
                                        label={index === 0 ? 'Environment' : ''}
                                        className="tw-flex-1"
                                        placeholder="Select"
                                        usePortal
                                        disabled={disabled || !row.channel}
                                        options={getEnvOptions(row)}
                                        value={row.env}
                                        onChange={(value) => handleEnvChange(row.id, String(value))}
                                        data-testid={`connectors-sync-row-${row.id}-env`}
                                    />
                                    <button
                                        type="button"
                                        aria-label="Remove channel-environment"
                                        data-testid={`connectors-sync-row-${row.id}-remove`}
                                        onClick={() => removeRow(row.id)}
                                        disabled={disabled || rows.length === 1}
                                        className={`tw-mb-2 tw-border-0 tw-bg-transparent tw-text-xl tw-leading-none ${
                                            disabled || rows.length === 1
                                                ? 'tw-text-ink-disabled tw-cursor-not-allowed'
                                                : 'tw-text-ink-secondary hover:tw-text-tone-danger-glyph tw-cursor-pointer'
                                        }`}
                                    >
                                        &times;
                                    </button>
                                </div>
                            ))}

                            <button
                                type="button"
                                onClick={addRow}
                                disabled={disabled || !canAddRow}
                                data-testid="connectors-sync-add-row"
                                className={`tw-border-0 tw-bg-transparent tw-font-medium tw-p-0 ${
                                    disabled || !canAddRow ? 'tw-text-ink-disabled tw-cursor-not-allowed' : 'tw-text-accent tw-cursor-pointer'
                                }`}
                            >
                                + Add more channel
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SyncToEnvironments;
